import gsap from 'gsap';
import PropTypes from 'prop-types';
import { useContext, useEffect, useRef, useState } from 'react';

import { CruiseSearchContext } from 'contexts/CruiseSearchContext';

import differenceInDays from 'date-fns/differenceInDays';
import differenceInHours from 'date-fns/differenceInHours';
import differenceInMilliseconds from 'date-fns/differenceInMilliseconds';
import TimeFormat from 'hh-mm-ss';

import { BRAND_CEL } from 'contexts/CruiseSearchContext/constants';
import styled from 'styled-components';
import colors from 'styles/colors';
import fonts from 'styles/fonts';
import sizes from 'styles/sizes';
import { PROMO_COUNTDOWN } from './constants';

const ButtonWrapper = styled.div`
	border-top: 1px solid ${colors.secondary.deepCove};
	width: 175px 10.9375rem;
	display: flex;
	justify-content: center;
	margin-top: 10px;
`;

const Button = styled.button`
	background-color: transparent;
	display: flex;
	align-items: center;
	border-radius: 5px;
	cursor: pointer;
	width: 50px;
	opacity: 0;
	overflow: hidden;
	margin-bottom: 10px;
	padding: 3px 0;
	@media (min-width: ${sizes.media.xSmallMaxWidth}px) {
		margin-bottom: 0px;
		padding: 0;
	}
	@media (max-width: ${sizes.media.xSmallMaxWidth}px) {
		justify-content: center;
		flex-wrap: ${(props) => (props.extended ? 'wrap' : 'no-wrap')};
	}
`;

const ExtendedLabel = styled.span`
	font-family: ${fonts.secondary.optimized};
	text-transform: uppercase;
	color: ${colors.secondary.deepCove};
	letter-spacing: 3px;
	font-size: 18px;
	margin-left: 0;
	width: 100%;
	display: block;
	@media (min-width: ${sizes.media.xSmallMaxWidth}px) {
		display: inline-block;
		width: auto;
		margin-left: 25px;
	}
`;

const Division = styled.span`
	font-family: ${fonts.primary.bold};
	font-size: 12px;
	line-height: 19px;
	color: ${colors.secondary.deepCove};
	margin: 0 10px;
	display: none;
	@media (min-width: ${sizes.media.xSmallMaxWidth}px) {
		display: inline-block;
	}
`;

const Label = styled.span`
	${(props) =>
		props.perkLockupStyle && props.brand == BRAND_CEL.code
			? `
  font-family: ${fonts.celebrity.poppins};
  color: ${colors.celebrity.blue};
  line-height: 21px;
  `
			: `
  font-family: ${fonts.primary.bold};
  color: ${colors.secondary.deepCove};
  line-height: 17px;
  `}

	margin-right: 5px;
	flex-shrink: 0;
	opacity: 0;
	transform: translateX(-5px);
	font-size: 14px;
	&:first-child {
		margin-left: 25px;
	}
	&:last-child {
		margin-right: ${(props) => (props.extended ? '0' : '25px')};
	}
	@media (min-width: ${sizes.media.xSmallMaxWidth}px) {
		&:last-child {
			margin-right: 25px;
		}
	}
`;

/**
 * PromoCountdown
 * Displays a countdown timer for the current Promo, if any.
 *
 * @param {string} endDate - Format: '2022-03-29T03:55:04.559Z'; determines how much time is left on the countdown.
 */

function PromoCountdown({ endDate, extended, perkLockupStyle, brand }) {
	const [countdown, setCountdown] = useState();
	const { globalization } = useContext(CruiseSearchContext);
	const { generalDictionary } = globalization?.dictionaries || {};
	const { labelPromoHeroDaysLeft, labelPromoHeroHours, labelPromoHeroMinutes, labelPromoHeroSalesEndIn, labelPromoHeroSeconds } = generalDictionary;

	const interval = useRef(null);
	const animatedIn = useRef(false);
	const wrapperRef = useRef();

	useEffect(
		() => () => {
			if (interval.current) {
				clearInterval(interval.current);
			}
		},
		[],
	);

	useEffect(() => {
		const makeNextCountdown = () => {
			let endDateWithoutTimezone = endDate ? new Date(endDate) : '';
			const date = new Date(endDateWithoutTimezone);
			const now = new Date();
			const days = differenceInDays(date, now);
			const fullDiff = differenceInMilliseconds(date, now);
			const timeDiff = TimeFormat.fromMs(fullDiff, 'hh:mm:ss');
			const [hrs, mins, secs] = timeDiff.split(':');
			const hours = Number(hrs);
			const minutes = Number(mins);
			const seconds = Math.round(Number(secs));
			const upDays = differenceInHours(date, now) < 72;

			setCountdown({
				days,
				hours,
				minutes,
				seconds,
				upDays,
			});
		};

		const { current } = interval || {};

		if (current) {
			clearInterval(current);
			makeNextCountdown();
		}

		interval.current = setInterval(makeNextCountdown, 1000);

		return () => {
			if (!current) return;
			clearInterval(current);
		};
	}, [endDate]);

	useEffect(() => {
		if (!countdown) return;

		const els = wrapperRef.current?.children || [];
		const tl = gsap.timeline({
			onComplete: () => {
				animatedIn.current = true;
			},
		});

		tl.to(
			wrapperRef.current,
			{
				width: 'auto',
				minHeight: '32px',
				marginTop: '4px',
				opacity: 1,
				duration: 0.3,
				ease: 'cubic-bezier(0,0,0,0)',
			},
			'together',
		).to(
			els,
			{
				x: 0,
				opacity: 1,
				duration: 0.1,
				stagger: 0.05,
				ease: 'cubic-bezier(0,0,0,0)',
			},
			'together',
		);
	}, [countdown]);

	const { days, hours, minutes, seconds, upDays } = countdown || {};

	const renderTimer = (extended) => {
		if (!countdown) {
			return;
		}
		return (
			<>
				{typeof hours === 'number' && (
					<Label perkLockupStyle={perkLockupStyle} brand={brand}>
						{hours} {labelPromoHeroHours}
					</Label>
				)}
				{typeof minutes === 'number' && (
					<Label perkLockupStyle={perkLockupStyle} brand={brand}>
						{minutes} {labelPromoHeroMinutes}
					</Label>
				)}
				{typeof seconds === 'number' && (
					<Label extended={extended} perkLockupStyle={perkLockupStyle} brand={brand}>
						{seconds} {labelPromoHeroSeconds}
					</Label>
				)}
			</>
		);
	};

	return (
		<ButtonWrapper>
			<Button extended={extended} type="button" ref={wrapperRef} id={PROMO_COUNTDOWN}>
				{extended && (
					<>
						<ExtendedLabel>Extended</ExtendedLabel>
						<Division>|</Division>
					</>
				)}
				<Label extended={extended} perkLockupStyle={perkLockupStyle} brand={brand}>
					{upDays ? labelPromoHeroSalesEndIn : labelPromoHeroDaysLeft.replace('{0}', days)}
				</Label>
				{upDays ? renderTimer(extended) : []}
			</Button>
		</ButtonWrapper>
	);
}

PromoCountdown.propTypes = {
	endDate: PropTypes.string,
	extended: PropTypes.bool,
};

export default PromoCountdown;
